/**
 * Water Feature Generation
 * Rivers, streams, and coastlines.
 */

import type { WaterFeature, District, Point, TerrainConfig } from './types';
import { SeededRandom } from './random';
import { smoothPolyline, fromPolar } from './geometry';

// ============================================================================
// WATER GENERATION
// ============================================================================

export function generateWater(
  terrain: TerrainConfig,
  districts: District[],
  rng: SeededRandom,
): WaterFeature[] {
  const features: WaterFeature[] = [];
  let waterIdx = 0;

  // Find settlement extent
  let maxExtent = 0;
  for (const d of districts) {
    for (const p of d.boundary) {
      const dist = Math.sqrt(p.x * p.x + p.y * p.y);
      if (dist > maxExtent) maxExtent = dist;
    }
  }
  const extent = maxExtent + 40;

  if (terrain.hasRiver) {
    // River: sinuous curve passing through/beside the settlement
    const entryAngle = rng.range(Math.PI * 0.6, Math.PI * 1.0);  // enters from upper-left area
    const exitAngle = entryAngle + Math.PI + rng.range(-0.5, 0.5); // exits roughly opposite

    const entryPoint = fromPolar(entryAngle, extent);
    const exitPoint = fromPolar(exitAngle, extent);

    // Generate waypoints with curves
    const waypoints: Point[] = [entryPoint];
    const steps = 5 + rng.int(0, 3);
    for (let i = 1; i < steps; i++) {
      const t = i / steps;
      const baseX = entryPoint.x + (exitPoint.x - entryPoint.x) * t;
      const baseY = entryPoint.y + (exitPoint.y - entryPoint.y) * t;
      waypoints.push({
        x: baseX + rng.range(-20, 20),
        y: baseY + rng.range(-20, 20),
      });
    }
    waypoints.push(exitPoint);

    features.push({
      id: `water_${waterIdx++}`,
      type: 'river',
      points: smoothPolyline(waypoints, 2),
      width: rng.range(6, 12),
    });

    // Sometimes add a stream tributary
    if (rng.bool(0.4)) {
      const branchPoint = waypoints[rng.int(1, waypoints.length - 2)];
      const streamAngle = rng.angle();
      const streamEnd = fromPolar(streamAngle, rng.range(40, 80), branchPoint);
      features.push({
        id: `water_${waterIdx++}`,
        type: 'stream',
        points: smoothPolyline([streamEnd, branchPoint], 1),
        width: rng.range(2, 4),
      });
    }
  }

  if (terrain.isCoastal) {
    // Coast: boundary along one edge
    const coastAngle = rng.range(0, Math.PI * 2);
    const coastPoints: Point[] = [];
    const coastSegments = 8;

    for (let i = 0; i <= coastSegments; i++) {
      const t = i / coastSegments;
      const angle = coastAngle - Math.PI / 3 + t * (Math.PI * 2 / 3);
      const r = extent + rng.range(-5, 5);
      coastPoints.push(fromPolar(angle, r));
    }

    features.push({
      id: `water_${waterIdx++}`,
      type: 'coast',
      points: smoothPolyline(coastPoints, 1),
      width: 20,
    });
  }

  return features;
}
